import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Mic,
  MicOff,
  X,
  Loader2,
  Search,
  Globe,
  TrendingUp,
  Users,
  ExternalLink,
  ChevronRight,
  Volume2,
  Sparkles,
  Filter,
  BarChart3,
  DollarSign,
  FileText
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

// Get API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.API_BASE_URL || 'https://mars2.mediacle.net';

// Suggested voice commands
const VOICE_SUGGESTIONS = [
  { icon: TrendingUp, text: 'Find high traffic affiliates', query: 'high traffic affiliates' },
  { icon: Globe, text: 'Affiliates in UK', query: 'affiliates in united kingdom' },
  { icon: Globe, text: 'Affiliates in Germany', query: 'affiliates in germany' },
  { icon: FileText, text: 'Top pages affiliates', query: 'affiliates with top pages' },
  { icon: Search, text: 'Top keywords affiliates', query: 'affiliates with top keywords' },
  { icon: DollarSign, text: 'High organic traffic', query: 'high organic traffic affiliates' },
];

const VoiceSearchPopup = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [error, setError] = useState(null);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [silenceTimer, setSilenceTimer] = useState(5);

  const recognitionRef = useRef(null);
  const popupRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const timerIntervalRef = useRef(null);
  const isListeningRef = useRef(false);

  // Handle search function
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) return;

    setIsProcessing(true);
    setError(null);
    setSearchResults(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/affiliates/voice-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ query: query.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        setSearchResults(data);
      } else {
        setError(data.error || 'Failed to search affiliates');
      }
    } catch (err) {
      console.error('Voice search error:', err);
      setError('Failed to connect to the server. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Initialize speech recognition once
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcriptText = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcriptText;
          } else {
            interimTranscript += transcriptText;
          }
        }

        // Update the displayed transcript
        const fullTranscript = finalTranscriptRef.current + finalTranscript + interimTranscript;
        setTranscript(fullTranscript);

        // Update the final transcript reference
        if (finalTranscript) {
          finalTranscriptRef.current += finalTranscript;
        }

        // Reset the 5-second timer every time we get new speech input
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }

        // Start countdown timer display
        setSilenceTimer(5);
        let countdown = 5;
        timerIntervalRef.current = setInterval(() => {
          countdown -= 1;
          setSilenceTimer(countdown);
          if (countdown <= 0) {
            clearInterval(timerIntervalRef.current);
          }
        }, 1000);

        // Set new 5-second timeout before searching
        const searchText = finalTranscriptRef.current + finalTranscript + interimTranscript;
        searchTimeoutRef.current = setTimeout(() => {
          if (searchText.trim() && isListeningRef.current) {
            // Stop listening and search
            isListeningRef.current = false;
            setIsListening(false);
            if (recognitionRef.current) {
              try {
                recognitionRef.current.stop();
              } catch (e) {
              }
            }
            handleSearch(searchText.trim());
          }
        }, 5000);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'not-allowed') {
          isListeningRef.current = false;
          setIsListening(false);
          setError('Microphone access denied. Please allow microphone access in your browser settings.');
        } else if (event.error === 'no-speech') {
          // Don't stop on no-speech, just show a message briefly
          setError('No speech detected. Keep speaking...');
          setTimeout(() => setError(null), 2000);
        } else if (event.error !== 'aborted') {
          isListeningRef.current = false;
          setIsListening(false);
          setError('Speech recognition error. Please try again.');
        }
      };

      recognitionRef.current.onend = () => {
        // If still supposed to be listening, restart recognition
        if (isListeningRef.current) {
          try {
            recognitionRef.current.start();
          } catch (e) {
            isListeningRef.current = false;
            setIsListening(false);
          }
        }
      };
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
        }
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [handleSearch]);

  // Close popup on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Cleanup when popup closes
  useEffect(() => {
    if (!isOpen) {
      // Stop recognition when popup closes
      if (recognitionRef.current && isListeningRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
        }
      }
      isListeningRef.current = false;
      setIsListening(false);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
  }, [isOpen]);

  const startListening = useCallback(() => {
    if (!recognitionRef.current) {
      setError('Speech recognition is not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    // Reset state
    setError(null);
    setTranscript('');
    setSearchResults(null);
    setSelectedAffiliate(null);
    setSilenceTimer(5);
    finalTranscriptRef.current = '';

    // Clear any existing timers
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    // Start listening
    isListeningRef.current = true;
    setIsListening(true);

    try {
      recognitionRef.current.start();
    } catch (e) {
      console.error('Failed to start recognition:', e);
      // It might already be running, try to stop and restart
      try {
        recognitionRef.current.stop();
        setTimeout(() => {
          try {
            recognitionRef.current.start();
          } catch (e2) {
            console.error('Failed to restart recognition:', e2);
            setError('Failed to start voice recognition. Please try again.');
            isListeningRef.current = false;
            setIsListening(false);
          }
        }, 100);
      } catch (e3) {
        setError('Failed to start voice recognition. Please try again.');
        isListeningRef.current = false;
        setIsListening(false);
      }
    }
  }, []);

  const stopListening = useCallback(() => {
    // Clear timers
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    isListeningRef.current = false;
    setIsListening(false);

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
      }

      // If there's transcript, search immediately
      const searchText = finalTranscriptRef.current || transcript;
      if (searchText.trim()) {
        handleSearch(searchText.trim());
      }
    }
  }, [transcript, handleSearch]);

  const handleSuggestionClick = (suggestion) => {
    setTranscript(suggestion.text);
    handleSearch(suggestion.query);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCurrency = (num) => {
    if (!num) return '$0';
    if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
    return '$' + num.toString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        ref={popupRef}
        className={`w-full max-w-3xl mx-4 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-gray-200'
        }`}
      >
        {/* Header */}
        <div className={`p-4 border-b ${isDark ? 'border-slate-700 bg-slate-800' : 'border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isListening
                  ? 'bg-red-500 animate-pulse'
                  : isDark ? 'bg-blue-600' : 'bg-gradient-to-r from-blue-600 to-purple-600'
              }`}>
                {isListening ? (
                  <Volume2 className="w-5 h-5 text-white animate-bounce" />
                ) : (
                  <Mic className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  Voice Search
                </h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {isListening
                    ? transcript
                      ? `Searching in ${silenceTimer}s... (keep talking or wait)`
                      : 'Listening... speak now'
                    : 'Search affiliates by voice'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Voice Input Section */}
          <div className="text-center mb-6">
            <button
              onClick={isListening ? stopListening : startListening}
              disabled={isProcessing}
              className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 ${
                isListening
                  ? 'bg-red-500 hover:bg-red-600 scale-110'
                  : isDark
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
              } ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {isProcessing ? (
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              ) : isListening ? (
                <MicOff className="w-8 h-8 text-white" />
              ) : (
                <Mic className="w-8 h-8 text-white" />
              )}
            </button>

            {transcript && (
              <div className={`p-4 rounded-xl mb-4 ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
                <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  "{transcript}"
                </p>
                {isListening && (
                  <div className="mt-3 flex items-center justify-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      silenceTimer <= 2 ? 'bg-red-500 text-white' : isDark ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {silenceTimer}
                    </div>
                    <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      seconds until search (keep talking to reset)
                    </span>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 mb-4">
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {!transcript && !searchResults && !isProcessing && (
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {isListening ? 'Speak now... (waits 5 seconds after you stop talking)' : 'Click the microphone to start'}
              </p>
            )}
          </div>

          {/* Suggestions */}
          {!searchResults && !isProcessing && !isListening && (
            <div className="mb-6">
              <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                <Sparkles className="w-4 h-4 inline mr-2" />
                Try saying:
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {VOICE_SUGGESTIONS.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={`flex items-center gap-2 p-3 rounded-xl text-left transition-all duration-200 ${
                      isDark
                        ? 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-100'
                    }`}
                  >
                    <suggestion.icon className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <span className="text-sm">{suggestion.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading */}
          {isProcessing && (
            <div className="text-center py-8">
              <Loader2 className={`w-12 h-12 mx-auto mb-4 animate-spin ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <p className={isDark ? 'text-slate-300' : 'text-gray-600'}>Searching affiliates...</p>
            </div>
          )}

          {/* Search Results */}
          {searchResults && !selectedAffiliate && (
            <div>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h4 className={`font-medium flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  <Filter className="w-4 h-4" />
                  Found
                  <span className={`px-2 py-0.5 rounded-full text-sm font-bold ${isDark ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'}`}>
                    {searchResults.data?.length || 0}
                  </span>
                  affiliates
                </h4>
                {searchResults.parsedQuery && (
                  <div className="flex flex-wrap gap-1">
                    {searchResults.parsedQuery.filters?.country && (
                      <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'}`}>
                        <Globe className="w-3 h-3 inline mr-1" />
                        {searchResults.parsedQuery.filters.country}
                      </span>
                    )}
                    {searchResults.parsedQuery.filters?.trafficType && (
                      <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                        <TrendingUp className="w-3 h-3 inline mr-1" />
                        {searchResults.parsedQuery.filters.trafficType}
                      </span>
                    )}
                    {searchResults.parsedQuery.filters?.highTraffic && (
                      <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                        <BarChart3 className="w-3 h-3 inline mr-1" />
                        High Traffic
                      </span>
                    )}
                    {searchResults.parsedQuery.filters?.vertical && (
                      <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                        {searchResults.parsedQuery.filters.vertical}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {searchResults.data?.length > 0 ? (
                <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2">
                  {searchResults.data.map((affiliate, index) => (
                    <div
                      key={affiliate.id || index}
                      onClick={() => setSelectedAffiliate(affiliate)}
                      className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                        isDark
                          ? 'bg-slate-800 hover:bg-slate-700 border border-slate-700'
                          : 'bg-gray-50 hover:bg-gray-100 border border-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {/* Rank Badge */}
                          <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                            index < 3
                              ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white'
                              : isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'
                          }`}>
                            #{index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                              {affiliate.url || affiliate.affiliate_url}
                            </h5>
                            <div className="flex items-center gap-4 mt-2 flex-wrap">
                              <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                <TrendingUp className="w-3 h-3 inline mr-1" />
                                {formatNumber(affiliate.totalTraffic || affiliate.total_traffic)} traffic
                              </span>
                              <span className={`text-sm text-green-500`}>
                                Organic: {formatNumber(affiliate.organicTraffic || affiliate.organic_traffic)}
                              </span>
                              <span className={`text-sm text-orange-500`}>
                                Paid: {formatNumber(affiliate.paidTraffic || affiliate.adwords_traffic)}
                              </span>
                              <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                <Globe className="w-3 h-3 inline mr-1" />
                                {affiliate.topGeos?.slice(0, 2).join(', ') || affiliate.top_geos?.split(',').slice(0, 2).join(', ') || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No affiliates found matching your query</p>
                  <p className="text-sm mt-2">Try different keywords or filters</p>
                </div>
              )}
            </div>
          )}

          {/* Affiliate Detail View */}
          {selectedAffiliate && (
            <div>
              <button
                onClick={() => setSelectedAffiliate(null)}
                className={`mb-4 text-sm flex items-center gap-1 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                Back to results
              </button>

              <div className={`p-6 rounded-xl ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-gray-50 border border-gray-100'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {selectedAffiliate.url || selectedAffiliate.affiliate_url}
                    </h4>
                    <a
                      href={`https://${selectedAffiliate.url || selectedAffiliate.affiliate_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-sm flex items-center gap-1 mt-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
                    >
                      Visit website <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  {selectedAffiliate.verticals?.length > 0 && (
                    <div className="flex gap-1">
                      {selectedAffiliate.verticals.map((v, i) => (
                        <span key={i} className={`px-2 py-1 text-xs rounded-full ${isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          {v}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-white'}`}>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Traffic</p>
                    <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {formatNumber(selectedAffiliate.totalTraffic || selectedAffiliate.total_traffic)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-white'}`}>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Organic Traffic</p>
                    <p className={`text-lg font-bold text-green-500`}>
                      {formatNumber(selectedAffiliate.organicTraffic || selectedAffiliate.organic_traffic)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-white'}`}>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Paid Traffic</p>
                    <p className={`text-lg font-bold text-orange-500`}>
                      {formatNumber(selectedAffiliate.paidTraffic || selectedAffiliate.adwords_traffic)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-white'}`}>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Organic Keywords</p>
                    <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {formatNumber(selectedAffiliate.organicKeywords || selectedAffiliate.organic_keywords)}
                    </p>
                  </div>
                </div>

                {/* Cost Metrics */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-white'}`}>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Organic Cost</p>
                    <p className={`text-lg font-bold text-green-500`}>
                      {formatCurrency(selectedAffiliate.organicCost || selectedAffiliate.organic_cost)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-white'}`}>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Paid Cost</p>
                    <p className={`text-lg font-bold text-orange-500`}>
                      {formatCurrency(selectedAffiliate.paidCost || selectedAffiliate.adwords_cost)}
                    </p>
                  </div>
                </div>

                {/* Geo Breakdown */}
                {(selectedAffiliate.geoBreakdown?.length > 0 || selectedAffiliate.top_geos) && (
                  <div className="mb-6">
                    <h5 className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                      <Globe className="w-4 h-4 inline mr-2" />
                      Geographic Distribution
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedAffiliate.geoBreakdown ? (
                        selectedAffiliate.geoBreakdown.slice(0, 5).map((geo, index) => (
                          <span
                            key={index}
                            className={`px-3 py-1 rounded-full text-sm ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'}`}
                          >
                            {geo.code || geo.country}: {formatNumber(geo.organic || 0)}
                          </span>
                        ))
                      ) : (
                        selectedAffiliate.top_geos?.split(',').slice(0, 5).map((geo, index) => (
                          <span
                            key={index}
                            className={`px-3 py-1 rounded-full text-sm ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'}`}
                          >
                            {geo.trim()}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Top Pages */}
                {(selectedAffiliate.topPages?.all?.length > 0 || selectedAffiliate.top_page) && (
                  <div className="mb-6">
                    <h5 className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                      <FileText className="w-4 h-4 inline mr-2" />
                      Top Pages
                    </h5>
                    <div className="space-y-2">
                      {selectedAffiliate.topPages?.all ? (
                        selectedAffiliate.topPages.all.slice(0, 3).map((page, index) => (
                          <div key={index} className={`text-sm truncate ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                            {page}
                          </div>
                        ))
                      ) : (
                        selectedAffiliate.top_page?.split(',').slice(0, 3).map((page, index) => (
                          <div key={index} className={`text-sm truncate ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                            {page.trim()}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Top Keywords */}
                {(selectedAffiliate.topKeywords?.all?.length > 0 || selectedAffiliate.keywords) && (
                  <div>
                    <h5 className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                      <BarChart3 className="w-4 h-4 inline mr-2" />
                      Top Keywords
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedAffiliate.topKeywords?.all ? (
                        selectedAffiliate.topKeywords.all.slice(0, 6).map((kw, index) => (
                          <span
                            key={index}
                            className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'}`}
                          >
                            {kw.keyword}
                          </span>
                        ))
                      ) : (
                        selectedAffiliate.keywords?.split(',').slice(0, 6).map((kw, index) => (
                          <span
                            key={index}
                            className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'}`}
                          >
                            {kw.trim()}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${isDark ? 'border-slate-700 bg-slate-800' : 'border-gray-100 bg-gray-50'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              Press Escape to close
            </span>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                Powered by AI
              </span>
              <Sparkles className={`w-3 h-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceSearchPopup;
